import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AlertCircle } from 'lucide-react';

export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signIn, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Redirect already logged-in users to assessment start
  if (!authLoading && user) {
    return <Navigate to="/assessment/start" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { data, error } = await signIn(email, password);

      if (error) {
        setError(error.message || 'Innlogging feilet');
        setLoading(false);
        return;
      }

      if (data?.user) {
        navigate('/assessment/start', { replace: true });
      } else {
        setLoading(false);
      }
    } catch (err: any) {
      setError(err.message || 'En feil oppstod under innlogging');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex flex-col items-center justify-center">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-8">
        <img 
          src="/logo_light.png" 
          alt="Helsenøkkelen" 
          className="h-12 w-auto dark:hidden"
        />
        <img 
          src="/logo_dark.png" 
          alt="Helsenøkkelen" 
          className="h-12 w-auto hidden dark:block"
        />
      </div>
      
      <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-sm border dark:border-gray-700">
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-900 dark:text-white">Logg inn</h2>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              E-post
            </label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Passord
            </label>
            <input
              id="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 dark:bg-blue-500 text-white py-2 rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 dark:hover:bg-blue-600 disabled:bg-blue-400 dark:disabled:bg-blue-700 disabled:cursor-not-allowed"
          >
            {loading ? 'Logger inn...' : 'Logg inn'}
          </button>
        </form>

        <div className="mt-4 text-center">
          <a 
            href="/forgot-password" 
            className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
          >
            Glemt passord?
          </a>
        </div>

        <p className="mt-4 text-center text-sm text-gray-600 dark:text-gray-400">
          Har du ikke en konto? Ta kontakt med Helsenøkkelen for å få vite mer.{' '}
          <a 
            href="mailto:post@helsenokkelen.no" 
            className="text-blue-600 dark:text-blue-400 hover:underline"
          >
            post@helsenokkelen.no
          </a>
        </p>
      </div>
      </div>
    </div>
  );
}